import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, BookOpen, Search, Globe } from 'lucide-react';
import StoryCard from '../components/StoryCard';
import { storyApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface Story {
  id: string;
  title: string;
  coverUrl?: string;
  status: 'DRAFT' | 'REVIEWING' | 'COMPLETED' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';
  createdAt: string;
  pageCount: number;
  isPublic?: boolean;
  theme?: string;
  tags?: string[];
}

const filters = [
  { value: 'all', label: '全部' },
  { value: 'approved', label: '已完成' },
  { value: 'reviewing', label: '生成中' },
];

const categoryFilters = [
  { value: 'all', label: '全部主题' },
  { value: 'adventure', label: '冒险' },
  { value: 'friendship', label: '友谊' },
  { value: 'space', label: '太空' },
  { value: 'dinosaur', label: '恐龙' },
  { value: 'nature', label: '自然' },
];

function Library() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchStories() {
      if (!isAuthenticated || !user?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await storyApi.getUserStories(user.id);
        const data = res.data?.data || [];
        setStories(data);
      } catch (err) {
        setError('获取绘本列表失败，请稍后重试');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchStories();
  }, [isAuthenticated, user?.id]);

  const handleToggleVisibility = async (storyId: string, isPublic: boolean) => {
    try {
      await storyApi.toggleVisibility(storyId, isPublic);
      setStories(prev => prev.map(s => s.id === storyId ? { ...s, isPublic } : s));
    } catch (err: any) {
      alert(err.response?.data?.error || '设置失败');
    }
  };

  const filteredStories = stories.filter((story) => {
    if (searchQuery && !story.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (activeFilter !== 'all' && story.status.toLowerCase() !== activeFilter) {
      return false;
    }
    if (activeCategory !== 'all') {
      const storyTags = story.tags || (story.theme ? [story.theme] : []);
      if (!storyTags.includes(activeCategory)) return false;
    }
    if (activeCategory !== 'all' && story.theme !== activeCategory) {
      return false;
    }
    return true;
  });

  return (
    <div className="w-full flex flex-col items-center py-8 lg:py-12">
      <div className="w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* 头部 */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-1">
            我的绘本库
          </h1>
          <p className="text-slate-500 text-sm md:text-base">共 {stories.length} 本绘本</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/public')}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-medium transition-all px-4 py-2"
          >
            <Globe className="w-5 h-5" />
            公开社区
          </button>
          <button
            onClick={() => navigate('/create')}
            className="hidden md:inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl font-medium transition-all px-4 py-2"
          >
            <Plus className="w-5 h-5" />
            创作新绘本
          </button>
        </div>
      </div>

      {/* 搜索和筛选 */}
      <div className="flex flex-col gap-4 mb-8">
        {/* 搜索框 */}
        <div className="relative max-w-[400px]">
          <div className="flex items-center gap-2 h-12 bg-white border border-slate-200 rounded-xl overflow-hidden focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 transition-all px-3">
            <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索绘本..."
              className="flex-1 bg-transparent text-slate-700 placeholder:text-slate-400 focus:outline-none"
            />
          </div>
        </div>

        {/* 状态筛选 */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-sm text-slate-500 whitespace-nowrap">状态:</span>
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`rounded-full text-sm font-medium whitespace-nowrap transition-all px-5 py-2.5 ${
                activeFilter === filter.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* 主题分类筛选 */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <span className="text-sm text-slate-500 whitespace-nowrap">主题:</span>
          {categoryFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveCategory(filter.value)}
              className={`rounded-full text-sm font-medium whitespace-nowrap transition-all px-5 py-2.5 ${
                activeCategory === filter.value
                  ? 'bg-violet-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* 内容区 */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm border border-slate-200">
              <div className="aspect-[4/5] sm:aspect-[3/4] bg-slate-200 animate-shimmer" />
              <div className="p-3 sm:p-4 md:p-5 flex flex-col gap-1.5 sm:gap-2">
                <div className="h-4 sm:h-5 bg-slate-200 rounded animate-shimmer w-3/4" />
                <div className="h-3 sm:h-4 bg-slate-200 rounded animate-shimmer w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : !isAuthenticated ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">请先登录</h3>
          <p className="text-slate-500">登录后查看您的绘本作品</p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl font-medium transition-all px-4 py-2"
          >
            去登录
          </button>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center">
            <span className="text-4xl">⚠️</span>
          </div>
          <h3 className="text-xl font-bold text-slate-800">出错了</h3>
          <p className="text-slate-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl font-medium transition-all px-4 py-2"
          >
            重新加载
          </button>
        </div>
      ) : filteredStories.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          {filteredStories.map((story) => (
            <StoryCard
              key={story.id}
              story={story}
              onClick={() => navigate(`/reader/${story.id}`)}
              onRegenerate={() => alert('重新生成图片')}
              showVisibilityToggle={true}
              onToggleVisibility={handleToggleVisibility}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <div className="w-24 h-24 rounded-2xl bg-slate-100 flex items-center justify-center">
            <BookOpen className="w-12 h-12 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">暂无绘本</h3>
          <p className="text-slate-500">创建您的第一本AI绘本吧！</p>
          <button
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl font-medium transition-all px-4 py-2"
          >
            开始创作
          </button>
        </div>
      )}

        {/* 移动端创作入口已移至 BottomNavigation */}
      </div>
    </div>
  );
}

export default Library;
